/**
 * Analyses API Service
 * Handles analysis-related API calls
 */

import { apiClient } from './client';
import type {
  Analysis,
  AnalysisListResponse,
  AnalysisProgressResponse,
  CreateAnalysisRequest,
  PremiumUpgradeResponse,
  ProcessingRequest,
} from '@/types/api';

/**
 * Analyses API service
 */
export const analysesService = {
  /**
   * List all analyses for the current user
   */
  async listAnalyses(page = 1, pageSize = 10): Promise<AnalysisListResponse> {
    return apiClient.get<AnalysisListResponse>(
      `/analyses?page=${page}&pageSize=${pageSize}`
    );
  },

  /**
   * Get a single analysis by ID
   */
  async getAnalysis(id: string): Promise<Analysis> {
    return apiClient.get<Analysis>(`/analyses/${id}`);
  },

  /**
   * Create a new analysis
   */
  async createAnalysis(data: CreateAnalysisRequest): Promise<Analysis> {
    return apiClient.post<Analysis>('/analyses', data);
  },

  /**
   * Get analysis progress (for polling during processing)
   */
  async getAnalysisProgress(id: string): Promise<AnalysisProgressResponse> {
    return apiClient.get<AnalysisProgressResponse>(`/analyses/${id}/progress`);
  },

  /**
   * Upgrade analysis to premium
   */
  async upgradeToPremium(id: string): Promise<PremiumUpgradeResponse> {
    return apiClient.post<PremiumUpgradeResponse>(`/analyses/${id}/premium`);
  },

  /**
   * Request a custom project plan (sends email to admin + confirmation to user)
   */
  async requestProjectPlan(ccEmails?: string[], requestId?: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/api/Requests/project-plan', { ccEmails, requestId });
  },

  /**
   * Request premium measures unlock (sends email to admin + confirmation to user)
   */
  async requestPremiumMeasures(ccEmails?: string[], requestId?: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/api/Requests/premium-measures', { ccEmails, requestId });
  },

  /**
   * Request eclou.analyst contact (sends email to admin + confirmation to user)
   */
  async requestAnalystContact(ccEmails?: string[]): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/api/Requests/analyst-contact', { ccEmails });
  },

  /**
   * Request a premium analysis offer (sends email to admin + confirmation to user)
   */
  async requestPremiumAnalysis(ccEmails?: string[], requestId?: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/api/Requests/premium-analysis', { ccEmails, requestId });
  },

  /**
   * List processing requests for the current user
   * Note: API returns array directly, not wrapped in ProcessingRequestListResponse
   */
  async listProcessingRequests(page = 1, pageSize = 3): Promise<ProcessingRequest[]> {
    return apiClient.get<ProcessingRequest[]>(
      `/api/Requests/my?page=${page}&pageSize=${pageSize}`
    );
  },

  /**
   * List processing requests with filtering and sorting options
   * @param options - Filter and pagination options
   */
  async listProcessingRequestsFiltered(options: {
    page?: number;
    pageSize?: number;
    status?: number | null;
    sortBy?: 'createdAt';
    sortOrder?: 'asc' | 'desc';
  }): Promise<{ requests: ProcessingRequest[]; total: number }> {
    const { page = 1, pageSize = 16, status, sortBy = 'createdAt', sortOrder = 'desc' } = options;

    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    params.append('sortBy', sortBy);
    params.append('sortOrder', sortOrder);

    if (status !== null && status !== undefined) {
      params.append('status', status.toString());
    }

    let requests = await apiClient.get<ProcessingRequest[]>(
      `/api/Requests/my?${params.toString()}`
    );

    // Apply client-side sorting to ensure correct order
    // (in case backend doesn't support sorting)
    requests = [...requests].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    // API returns array directly, we wrap it for pagination support
    // Note: If API provides total count in headers or response, update this
    return { requests, total: requests.length };
  },
};
